import { sortBy } from "lodash-es";
import { defineStore } from "pinia";
import { computed, reactive, ref, toRefs, watch } from "vue";

import type { Sticker } from "@/model/StickerType";
import type { Color, IdMap, InfoLevel, StatusClass } from "@/model/baseTypes";
import { statusClasses } from "@/model/baseTypes";
import type { BoardCard } from "@/model/card";
import { statusClassColors } from "@/model/colors";
import type { DependencyTeamFilter, DependencyTeamItem } from "@/model/search";
import type { Iteration, Team } from "@/model/session";
import type { StickyType } from "@/model/stickyType";
import { isDependency } from "@/model/stickyType";
import type { AuthUser } from "@/model/user";
import { useGlobalI18n } from "@/translations/i18n";
import { toCssColor, white } from "@/utils/color";
import { parseStickerString, stickerToFilterKey } from "@/utils/sticker";
import type { FilterProps } from "@/utils/type";

import { useArtStore } from "../art";
import { useBoardStore } from "../board";
import { riskyLinkStates } from "../helpers/linkState";
import { toggleFunc } from "../helpers/toggle";
import { useSessionStore } from "../session";
import { isNotUserTeam, NO_TEAM, useTeamStore } from "../team";
import { noAssignee, noReporter, useUserStore } from "../user";
import type { SignalId } from "./signalDefinitions";
import { boardSignals } from "./signalDefinitions";

export const dependencyLinks = ["Linked", "Not Linked"];
type DependencyLink = (typeof dependencyLinks)[number];

const { t } = useGlobalI18n();

interface DependencyLinkItem {
  id: DependencyLink;
  name: string;
}

export const dependencyLinkItems: DependencyLinkItem[] = [
  { id: "Linked", name: /*$t*/ "dependency.linked" },
  { id: "Not Linked", name: /*$t*/ "dependency.notLinked" },
];

export type UpperLevelLinksSelection =
  | { type: "none" }
  | { type: "linked" }
  | { type: "unlinked" }
  | { type: "sticky"; stickyId: string };

type StatusDistributionSelection =
  | { type: "none" }
  | {
      type: "statusClass";
      statusClass: StatusClass;
      stickyIds: string[];
      parentStickyId?: string;
    };

export interface StatusItem {
  id: string;
  statusClass: StatusClass;
  name: string;
  color: Color | string;
  order: number;
}

export interface SearchMenuState {
  isSearchOpen: boolean;
}

export interface SearchState {
  text: string;
  selectedStickyTypeIds: Set<string>;
  selectedDependencyLink: Set<DependencyLink>;
  selectedLinkStates: Set<InfoLevel>;
  selectedSignals: Set<SignalId>;
  selectedAssigneeIds: Set<string>;
  selectedReporterIds: Set<string>;
  selectedTeamIds: Set<string>;
  selectedArtIds: Set<string>;
  selectedIterationIds: Set<number>;
  selectedFlagStrings: Set<string>;
  selectedStatusIds: Set<string>;
  selectedDependencyTeamFilter: DependencyTeamFilter;
  selectedUpperLevelLinks: UpperLevelLinksSelection;
  selectedStatusDistribution: StatusDistributionSelection;
}

export type UrlSyncedSearchState = Omit<
  SearchState,
  "selectedDependencyTeamFilter" | "selectedStatusDistribution"
>;

/** properties that hold a Set of filter values. */
export const setSearchProps = [
  "selectedStickyTypeIds",
  "selectedDependencyLink",
  "selectedLinkStates",
  "selectedSignals",
  "selectedAssigneeIds",
  "selectedReporterIds",
  "selectedTeamIds",
  "selectedArtIds",
  "selectedIterationIds",
  "selectedFlagStrings",
  "selectedStatusIds",
] satisfies Array<FilterProps<SearchState, Set<unknown>>>;

export const useSearchMenuStore = defineStore("searchMenu", () => {
  const isSearchOpen = ref(false);
  const search = reactive<SearchState>(emptySearchState());

  watch(
    () => search.selectedTeamIds.size,
    (size) => {
      if (size < 2 && search.selectedDependencyTeamFilter === "mutual") {
        search.selectedDependencyTeamFilter = "all";
      }
    },
  );

  const allUsers = computed(() => {
    const res: { assignee: IdMap<AuthUser>; reporter: IdMap<AuthUser> } = {
      assignee: {},
      reporter: {},
    };
    const cards = useBoardStore().currentBoard().cards;
    for (const type of ["assignee", "reporter"] as const) {
      for (const id in cards) {
        const user = cards[id].data[type];
        if (user) {
          res[type][user.id] = user;
        }
      }
      // Add the current user to the list
      // (so they can confirm they have no cards assigned/created)
      const currentUser = useUserStore().currentUser;
      if (currentUser) {
        res[type][currentUser.id] ??= currentUser;
      }
    }
    return {
      assignee: [...Object.values(res.assignee), noAssignee(toCssColor(white))],
      reporter: [...Object.values(res.reporter), noReporter(toCssColor(white))],
    };
  });

  const allAssignees = computed(() => allUsers.value.assignee);
  const allReporters = computed(() => allUsers.value.reporter);

  const allTeams = computed((): Team[] => {
    const teamStore = useTeamStore();
    const internal = sortBy(teamStore.teamsInCurrentArt, isNotUserTeam);
    // External orgs are only relevant to dependencies, so we surface them in the
    // team filter exclusively during a dependency search. They are tied to the
    // current ART (we only have dependent_team_id, not dependent_team_art_id) and
    // sorted alphabetically, appended after the regular teams.
    const externals = isDependencySearch.value
      ? sortBy(teamStore.externalsInCurrentArt, (team) => team.name.toLowerCase())
      : [];
    return [...internal, ...externals, teamStore.noTeam];
  });

  const teamIdsOfSelectedArts = computed(
    () =>
      new Set(
        useTeamStore()
          .teams.filter((team) => search.selectedArtIds.has(team.artId || ""))
          .map((team) => team.id),
      ),
  );

  const effectiveTeamIds = computed(() =>
    useBoardStore().currentBoard().type === "solution"
      ? teamIdsOfSelectedArts.value
      : search.selectedTeamIds,
  );

  const allArts = computed(() => [...useArtStore().arts, useArtStore().noArt]);

  const allStatusItems = computed(() => {
    const cards = useBoardStore().currentBoard().cards;

    // Collect all statuses with duplicates
    const all = Object.values(cards).flatMap((card) => {
      const status = card.data.status;
      return status
        ? [
            {
              id: `${status.statusClass}-${status.name}`,
              statusClass: status.statusClass,
              name: status.name,
              color: statusClassColors[status.statusClass],
              order: status.order,
            },
          ]
        : [];
    });

    // Deduplicate by `id`
    const uniqueMap = new Map(all.map((s) => [s.id, s]));
    const unique = Array.from(uniqueMap.values());

    // Two-level sorting to handle a mix of ALM and local status classes
    // (ALM-defined status classes have an order, local ones don't)
    return sortBy(unique, [
      (item) => statusClasses.indexOf(item.statusClass), // First group/sort by status class
      (item) => item.order, // Then follow backend-defined order within each class
    ]);
  });

  const allIterations = computed(() => useSessionStore().iterations);
  const hasUpperLevelLinksFilter = computed(() => search.selectedUpperLevelLinks.type !== "none");
  const hasStatusDistributionFilter = computed(
    () => search.selectedStatusDistribution.type !== "none",
  );
  // selected signals that apply to the current board; others stay selected (and in the URL) but inactive
  const activeSignals = computed(() => {
    const board = useBoardStore().board;
    if (!board) return [];
    return boardSignals(board).filter((signal) => search.selectedSignals.has(signal.id));
  });

  const anyFilterCriteria = computed(
    () =>
      search.text !== "" ||
      setSearchProps.some((k) =>
        k === "selectedSignals" ? activeSignals.value.length > 0 : search[k].size !== 0,
      ) ||
      hasUpperLevelLinksFilter.value ||
      hasStatusDistributionFilter.value,
  );

  const isDependencySearch = computed(() =>
    useBoardStore().possibleStickyTypes.some(
      (stickyType) =>
        stickyType.functionality === "dependency" &&
        search.selectedStickyTypeIds.has(stickyType.id),
    ),
  );

  const dependencyFilterItems = computed((): DependencyTeamItem[] => [
    {
      id: "all",
      name: t("depTeamFilter.all"),
      description: t("depTeamFilter.all.desc", { count: search.selectedTeamIds.size }),
    },
    {
      id: "blocked",
      name: t("depTeamFilter.blocked"),
      description: t("depTeamFilter.blocked.desc", { count: search.selectedTeamIds.size }),
    },
    {
      id: "enabling",
      name: t("depTeamFilter.enabling"),
      description: t("depTeamFilter.enabling.desc", { count: search.selectedTeamIds.size }),
    },
    ...(search.selectedTeamIds.size < 2
      ? []
      : [
          {
            id: "mutual" as const,
            name: t("depTeamFilter.mutual"),
            description: t("depTeamFilter.mutual.desc"),
          },
        ]),
  ]);

  // link-state filter for the link layer and sticky note links: selected link states or the risky links signal
  function matchesLinkStates(linkState: InfoLevel, emptyMatch = false) {
    const states = search.selectedLinkStates;
    const signalMatch = activeSignals.value.some((signal) => signal.id === "riskyLinks")
      ? riskyLinkStates.has(linkState)
      : emptyMatch && states.size === 0;
    return signalMatch || Array.from(states).some(matchesLinkState);

    function matchesLinkState(state: InfoLevel) {
      switch (state) {
        case "default":
          return true;
        case "warn":
          return linkState === "warn";
        case "error":
          return linkState === "error";
      }
    }
  }

  function showCardLinks(card: BoardCard) {
    const showForSelectedCard = useBoardStore().activeCardId === card.data.id;
    return (
      (isDependency(card.data) && isDependencySearch.value) ||
      showForSelectedCard ||
      card.meta.dragging ||
      card.meta.isHighlighted ||
      card.meta.isRelatedToHighlighted
    );
  }

  function toggleFlag(flag: Sticker | null) {
    const flagString = stickerToFilterKey(flag);
    if (search.selectedFlagStrings.has(flagString)) {
      search.selectedFlagStrings.delete(flagString);
    } else {
      search.selectedFlagStrings.add(flagString);
    }
    search.selectedFlagStrings = new Set(search.selectedFlagStrings);
  }

  function selectStickyTypes(stickyTypes: StickyType[]) {
    search.selectedStickyTypeIds = new Set(stickyTypes.map((type) => type.id));
    // External orgs are only selectable during a dependency search; drop any
    // existing external selections if dependency is not selected.
    if (!isDependencySearch.value && search.selectedTeamIds.size) {
      const teamStore = useTeamStore();
      search.selectedTeamIds = new Set(
        [...search.selectedTeamIds].filter((id) => !teamStore.isExternalTeam(id)),
      );
    }
  }

  return {
    // kept in state (not readonly) so searchBoardSync's $state watcher sees it
    isSearchOpen,
    ...toRefs(search),

    selectedRiskyLinks: computed(() => Array.from(search.selectedLinkStates)),
    selectedStickyTypes: computed(() =>
      useBoardStore().possibleStickyTypes.filter((type) =>
        search.selectedStickyTypeIds.has(type.id),
      ),
    ),
    selectedDependencyLinks: computed(() => Array.from(search.selectedDependencyLink)),
    selectedDependencyLinkItems: computed(() =>
      dependencyLinkItems.filter((item) => search.selectedDependencyLink.has(item.id)),
    ),
    allUsers,
    allAssignees,
    selectedAssignees: computed(() =>
      allAssignees.value.filter((assignee) => search.selectedAssigneeIds.has(assignee.id)),
    ),
    allReporters,
    selectedReporters: computed(() =>
      allReporters.value.filter((reporter) => search.selectedReporterIds.has(reporter.id)),
    ),
    allTeams,
    selectedTeams: computed(() =>
      allTeams.value.filter((team) => search.selectedTeamIds.has(team.id)),
    ),
    effectiveTeamIds,
    teamIdsOfSelectedArts,
    allArts,
    selectedArts: computed(() => allArts.value.filter((art) => search.selectedArtIds.has(art.id))),
    flags: computed(() => Array.from(search.selectedFlagStrings).map(parseStickerString)),
    allStatusItems,
    selectedStatusItems: computed(() =>
      allStatusItems.value.filter((item) => search.selectedStatusIds.has(item.id)),
    ),
    allIterations,
    selectedIterations: computed(() =>
      allIterations.value.filter((iteration) => search.selectedIterationIds.has(iteration.id)),
    ),
    hasUpperLevelLinksFilter,
    hasStatusDistributionFilter,
    anyFilterCriteria,
    activeSignals,
    isDependencySearch,
    dependencyFilterItems,
    selectedDependencyFilterItem: computed(
      () =>
        dependencyFilterItems.value.find(
          (item) => item.id === search.selectedDependencyTeamFilter,
        ) ?? dependencyFilterItems.value[0],
    ),
    showDependencyTeamFilter: computed(
      () => isDependencySearch.value && search.selectedTeamIds.size > 0,
    ),

    isTeamSelected: (teamId: Team["id"] | undefined) =>
      effectiveTeamIds.value.has(teamId || NO_TEAM.id),
    matchesLinkStates,
    showCardLinks,

    expandSearch: () => (isSearchOpen.value = true),
    toggleSearch: toggleFunc(isSearchOpen),
    resetCriteria: () => Object.assign(search, emptySearchState()),
    toggleFlag,
    clearFlags: () => (search.selectedFlagStrings = new Set()),
    selectIterations: (iterations: Iteration[]) =>
      (search.selectedIterationIds = new Set(iterations.map((i) => i.id))),
    selectStickyTypes,
  };
});

function emptySearchState() {
  return {
    text: "",
    ...Object.fromEntries(setSearchProps.map((k) => [k, new Set()])),
    selectedDependencyTeamFilter: "all",
    selectedUpperLevelLinks: { type: "none" },
    selectedStatusDistribution: { type: "none" },
  } as SearchState;
}
